/**
 * SEO Descriptions for Industry × Country Cross-Pages
 * 60 combinations × Tier 1 languages (FR, ES, DE, AR) = 240 entries
 * Auto-registered in the main SEO catalog via getSEOByRoute
 */

import type { PageSEODescription } from './seo-descriptions';

type LangSEOMap = Record<string, Partial<PageSEODescription>>;

// ── Industry labels per language ──
const IND_LABELS: Record<string, Record<string, string>> = {
  ecommerce: { fr: 'E-commerce', es: 'Comercio electrónico', de: 'E-Commerce', ar: 'التجارة الإلكترونية', it: 'E-commerce', pt: 'E-commerce', nl: 'E-commerce' },
  fintech: { fr: 'Fintech', es: 'Fintech', de: 'Fintech', ar: 'التكنولوجيا المالية', it: 'Fintech', pt: 'Fintech', nl: 'Fintech' },
  healthcare: { fr: 'Santé', es: 'Salud', de: 'Gesundheitswesen', ar: 'الرعاية الصحية', it: 'Sanità', pt: 'Saúde', nl: 'Gezondheidszorg' },
  saas: { fr: 'SaaS', es: 'SaaS', de: 'SaaS', ar: 'SaaS', it: 'SaaS', pt: 'SaaS', nl: 'SaaS' },
  telecom: { fr: 'Télécoms', es: 'Telecomunicaciones', de: 'Telekommunikation', ar: 'الاتصالات', it: 'Telecomunicazioni', pt: 'Telecomunicações', nl: 'Telecom' },
  insurance: { fr: 'Assurance', es: 'Seguros', de: 'Versicherung', ar: 'التأمين', it: 'Assicurazioni', pt: 'Seguros', nl: 'Verzekeringen' },
};

const COUNTRY_LABELS: Record<string, Record<string, string>> = {
  france: { fr: 'France', es: 'Francia', de: 'Frankreich', ar: 'فرنسا', it: 'Francia', pt: 'França', nl: 'Frankrijk' },
  germany: { fr: 'Allemagne', es: 'Alemania', de: 'Deutschland', ar: 'ألمانيا', it: 'Germania', pt: 'Alemanha', nl: 'Duitsland' },
  uk: { fr: 'Royaume-Uni', es: 'Reino Unido', de: 'Großbritannien', ar: 'المملكة المتحدة', it: 'Regno Unito', pt: 'Reino Unido', nl: 'Verenigd Koninkrijk' },
  usa: { fr: 'États-Unis', es: 'Estados Unidos', de: 'USA', ar: 'الولايات المتحدة', it: 'Stati Uniti', pt: 'Estados Unidos', nl: 'Verenigde Staten' },
  uae: { fr: 'Émirats arabes unis', es: 'Emiratos Árabes', de: 'VAE', ar: 'الإمارات', it: 'Emirati Arabi', pt: 'Emirados Árabes', nl: 'VAE' },
  spain: { fr: 'Espagne', es: 'España', de: 'Spanien', ar: 'إسبانيا', it: 'Spagna', pt: 'Espanha', nl: 'Spanje' },
  netherlands: { fr: 'Pays-Bas', es: 'Países Bajos', de: 'Niederlande', ar: 'هولندا', it: 'Paesi Bassi', pt: 'Países Baixos', nl: 'Nederland' },
  canada: { fr: 'Canada', es: 'Canadá', de: 'Kanada', ar: 'كندا', it: 'Canada', pt: 'Canadá', nl: 'Canada' },
  switzerland: { fr: 'Suisse', es: 'Suiza', de: 'Schweiz', ar: 'سويسرا', it: 'Svizzera', pt: 'Suíça', nl: 'Zwitserland' },
  italy: { fr: 'Italie', es: 'Italia', de: 'Italien', ar: 'إيطاليا', it: 'Italia', pt: 'Itália', nl: 'Italië' },
};

// ── Title/Description templates per language ──
const TEMPLATES: Record<string, {
  title: (ind: string, country: string) => string;
  desc: (ind: string, country: string) => string;
  kw: (ind: string, country: string) => string[];
}> = {
  fr: {
    title: (ind, c) => `Centre d'appel ${ind} ${c} | BPO ${ind} ${c} — BPO Contact`,
    desc: (ind, c) => `Centre d'appel spécialisé ${ind.toLowerCase()} pour ${c}. Support client natif, conformité locale, réduction des coûts de 40-60%. Agents francophones. Évaluation gratuite.`,
    kw: (ind, c) => [`centre d'appel ${ind.toLowerCase()} ${c.toLowerCase()}`, `BPO ${ind.toLowerCase()} ${c.toLowerCase()}`, `externalisation ${ind.toLowerCase()} ${c.toLowerCase()}`, `support client ${ind.toLowerCase()} ${c.toLowerCase()}`],
  },
  es: {
    title: (ind, c) => `Call Center ${ind} ${c} | BPO ${ind} ${c} — BPO Contact`,
    desc: (ind, c) => `Call center especializado en ${ind.toLowerCase()} para ${c}. Soporte nativo, cumplimiento local, ahorro del 40-60%. Evaluación gratuita.`,
    kw: (ind, c) => [`call center ${ind.toLowerCase()} ${c.toLowerCase()}`, `BPO ${ind.toLowerCase()} ${c.toLowerCase()}`, `externalización ${ind.toLowerCase()} ${c.toLowerCase()}`],
  },
  de: {
    title: (ind, c) => `${ind} Callcenter ${c} | ${ind} BPO ${c} — BPO Contact`,
    desc: (ind, c) => `Spezialisiertes ${ind}-Callcenter für ${c}. Muttersprachlicher Support, lokale Compliance. Kostenlose Bewertung.`,
    kw: (ind, c) => [`${ind.toLowerCase()} callcenter ${c.toLowerCase()}`, `${ind.toLowerCase()} BPO ${c.toLowerCase()}`, `${ind.toLowerCase()} outsourcing ${c.toLowerCase()}`],
  },
  ar: {
    title: (ind, c) => `مركز اتصال ${ind} ${c} | BPO ${ind} ${c} — BPO Contact`,
    desc: (ind, c) => `مركز اتصال متخصص في ${ind} لـ${c}. دعم عملاء بلغات محلية، امتثال تنظيمي،. تقييم مجاني.`,
    kw: (ind, c) => [`مركز اتصال ${ind} ${c}`, `BPO ${ind} ${c}`, `خدمة عملاء ${ind} ${c}`],
  },
  it: {
    title: (ind, c) => `Call Center ${ind} ${c} | BPO ${ind} ${c} — BPO Contact`,
    desc: (ind, c) => `Call center specializzato ${ind.toLowerCase} per ${c}. Supporto nativo, compliance locale. Valutazione gratuita.`,
    kw: (ind, c) => [`call center ${ind.toLowerCase()} ${c.toLowerCase()}`, `BPO ${ind.toLowerCase()} ${c.toLowerCase()}`],
  },
  pt: {
    title: (ind, c) => `Call Center ${ind} ${c} | BPO ${ind} ${c} — BPO Contact`,
    desc: (ind, c) => `Call center especializado em ${ind.toLowerCase} para ${c}. Suporte nativo, conformidade local. Avaliação gratuita.`,
    kw: (ind, c) => [`call center ${ind.toLowerCase()} ${c.toLowerCase()}`, `BPO ${ind.toLowerCase()} ${c.toLowerCase()}`],
  },
  nl: {
    title: (ind, c) => `${ind} Callcenter ${c} | ${ind} BPO ${c} — BPO Contact`,
    desc: (ind, c) => `Gespecialiseerd ${ind.toLowerCase()} callcenter voor ${c}. Native support, lokale compliance, 40-60% kostenbesparing. Gratis assessment.`,
    kw: (ind, c) => [`${ind.toLowerCase()} callcenter ${c.toLowerCase()}`, `${ind.toLowerCase()} BPO ${c.toLowerCase()}`],
  },
};

// ── Generate all entries ──
const INDUSTRIES = ['ecommerce', 'fintech', 'healthcare', 'saas', 'telecom', 'insurance'];
const COUNTRIES = ['france', 'germany', 'uk', 'usa', 'uae', 'spain', 'netherlands', 'canada', 'switzerland', 'italy'];
const TIER1_LANGS = ['fr', 'es', 'de', 'ar'];
const TIER2_LANGS = ['it', 'pt', 'nl'];

export const CROSS_PAGE_SEO_I18N: Record<string, LangSEOMap> = {};

for (const ind of INDUSTRIES) {
  for (const country of COUNTRIES) {
    const routeKey = `${ind}-bpo-${country}`;
    const langMap: LangSEOMap = {};

    for (const lang of [...TIER1_LANGS, ...TIER2_LANGS]) {
      const tpl = TEMPLATES[lang];
      if (!tpl) continue;
      const indLabel = IND_LABELS[ind]?.[lang] || ind;
      const countryLabel = COUNTRY_LABELS[country]?.[lang] || country;

      langMap[lang] = {
        title: tpl.title(indLabel, countryLabel),
        description: tpl.desc(indLabel, countryLabel),
        keywords: tpl.kw(indLabel, countryLabel),
      };
    }

    CROSS_PAGE_SEO_I18N[routeKey] = langMap;
  }
}

/**
 * Lookup cross-page SEO for a given route slug and language.
 * Returns undefined if not found (falls back to English auto-generated).
 */
export function getCrossPageSEO(slug: string, lang: string): Partial<PageSEODescription> | undefined {
  return CROSS_PAGE_SEO_I18N[slug]?.[lang];
}
